import { Box, TextField, Typography } from "@mui/material"
import type { Row } from "@tanstack/react-table"
import dayjs from "dayjs"
import type { FC } from "react"

import { type GlTransactionRow, GlTransactionStatus } from "@/graphql/codegen/graphql"
import { AccountDetailsField } from "@/screens/Accounting/components/AccountDetailsField"

interface TransactionDetailsRowProps {
  row: Row<GlTransactionRow>
}

const formatDate = (dateStr: string): string => dayjs(dateStr).format("YYYY/MM/DD")

export const TransactionDetailsRow: FC<TransactionDetailsRowProps> = ({ row }) => {
  const { glTransaction } = row.original
  const status = glTransaction?.status
  const postedBy = glTransaction?.createdByUser
    ? [glTransaction.createdByUser.firstName, glTransaction.createdByUser.lastName].filter(Boolean).join(" ")
    : ""

  const isReversed = status === GlTransactionStatus.Reversed
  const isCancelled = status === GlTransactionStatus.Cancelled

  const reverseTransaction = glTransaction?.reverseTransaction
  const reversalDate = reverseTransaction?.createdOn ? formatDate(reverseTransaction.createdOn) : "-"
  const reversedBy = reverseTransaction?.createdByUser
    ? [reverseTransaction.createdByUser.firstName, reverseTransaction.createdByUser.lastName].filter(Boolean).join(" ")
    : "-"
  const reversalReferenceId = reverseTransaction?.referenceNumber ?? "-"
  const reversalReason = glTransaction?.reason ?? "-"

  const cancelledDate = glTransaction?.updatedOn ? formatDate(glTransaction.updatedOn) : "-"
  const cancelledBy = glTransaction?.updatedByUser
    ? [glTransaction.updatedByUser.firstName, glTransaction.updatedByUser.lastName].filter(Boolean).join(" ")
    : "-"

  return (
    <Box className='flex gap-6 border-b border-gray-400 bg-primary-100 px-6'>
      <Box className='flex flex-1 flex-col gap-2 p-4'>
        <Typography variant='subtitle2' className='text-gray-700'>
          Transaction Details
        </Typography>
        <Box className='flex gap-6'>
          <Box className='flex flex-1 flex-col gap-4'>
            <AccountDetailsField label='Posted By' value={postedBy} />
            {isReversed && <AccountDetailsField label='Reversal Date' value={reversalDate} />}
            {isCancelled && <AccountDetailsField label='Cancelled Date' value={cancelledDate} />}
          </Box>

          <Box className='flex flex-1 flex-col gap-4'>
            <AccountDetailsField label='Reference' value={glTransaction?.referenceNumber ?? ""} />
            {isReversed && <AccountDetailsField label='Reversed By' value={reversedBy} />}
            {isCancelled && <AccountDetailsField label='Cancelled By' value={cancelledBy} />}
          </Box>

          <Box className='flex flex-1 flex-col gap-4'>
            {isReversed && (
              <>
                <AccountDetailsField label='Reversal Reference ID' value={reversalReferenceId} />
                <AccountDetailsField label='Reversal Reason' value={reversalReason} />
              </>
            )}
            {isCancelled && (
              <>
                <Box className='h-[44px]' />
                <AccountDetailsField label='Cancellation Reason' value={glTransaction?.reason ?? "-"} />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box className='flex flex-1 flex-col justify-center p-4'>
        <TextField
          label='Notes'
          multiline
          disabled
          fullWidth
          value={glTransaction?.note ?? ""}
          variant='outlined'
          size='small'
          data-testid={`transactions.expanded-row.notes-field-${row.original.glTransactionRowId}`}
        />
      </Box>
    </Box>
  )
}
